import type { PaddleTier } from '../../types';

interface StatusBadgeProps {
  type: 'status' | 'tier' | 'invoice' | 'job';
  value: string;
  className?: string;
}

export function StatusBadge({ type, value, className = '' }: StatusBadgeProps) {
  const getStatusStyles = () => {
    if (type === 'job') {
      switch (value) {
        case 'completed':
        case 'active':
          return 'bg-success/10 text-success border-success/20';
        case 'failed':
        case 'expired':
          return 'bg-destructive/10 text-destructive border-destructive/20';
        case 'processing':
        case 'queued':
        case 'analyzing':
          return 'bg-info/10 text-info border-info/20';
        case 'partial':
        case 'awaiting_confirmation':
          return 'bg-warning/10 text-warning border-warning/20';
        default:
          return 'bg-muted text-muted-foreground border-border';
      }
    }

    if (type === 'status') {
      switch (value) {
        case 'active':
          return 'bg-success/10 text-success border-success/20';
        case 'past_due':
          return 'bg-warning/10 text-warning border-warning/20';
        case 'canceled':
          return 'bg-destructive/10 text-destructive border-destructive/20';
        case 'paused':
          return 'bg-muted text-muted-foreground border-border';
        case 'trialing':
          return 'bg-primary/10 text-primary border-primary/20';
        default:
          return 'bg-muted text-muted-foreground border-border';
      }
    }

    if (type === 'tier') {
      const tierValue = value as PaddleTier;
      switch (tierValue) {
        case 'developer':
          return 'bg-muted text-muted-foreground border-border';
        case 'starter':
          return 'bg-info/10 text-info border-info/20';
        case 'growth':
          return 'bg-primary/15 text-primary border-primary/30';
        case 'professional':
          return 'bg-success/10 text-success border-success/20';
        case 'enterprise':
          return 'bg-secondary text-foreground border-border';
        default:
          return 'bg-muted text-muted-foreground border-border';
      }
    }

    if (type === 'invoice') {
      switch (value) {
        case 'completed':
        case 'paid':
          return 'bg-success/10 text-success border-success/20';
        case 'billed':
        case 'unpaid':
          return 'bg-warning/10 text-warning border-warning/20';
        case 'past_due':
          return 'bg-destructive/10 text-destructive border-destructive/20';
        case 'canceled':
          return 'bg-muted text-muted-foreground border-border';
        default:
          return 'bg-muted text-muted-foreground border-border';
      }
    }

    return 'bg-muted text-muted-foreground border-border';
  };

  const formatLabel = () => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusStyles()} ${className}`}
    >
      {formatLabel()}
    </span>
  );
}
